/**
 * The Health reports' files — one Excel workbook and one CSV per report and per level-1 grouping
 * (bata-reqs docs/25 §5.4 B2; ADR 9010, 9045, 9072; `docs/adr/3003`).
 *
 * Each file re-reads the SAME read its page reads, over the same scope (`?country=`, `?by=`,
 * `?department=`, the source the URL names), page after page in the platform's order, and writes one row
 * per group: its key and name, then for each of the report's measures its value, parts, target, colour and
 * why it is absent — so the file and the page cannot disagree. An absent figure leaves as `missing`, never 0;
 * a total the page shows as nought because nothing is known leaves as the platform sent it, with its reason.
 *
 * **The by-article file names its articles** (client request and owner answers 2026-11-03): brand, gender,
 * major category, category and risk beside each article's group, from `POST /health/attributes` on the
 * read's own date (`@/planning/health-attributes`). Export columns only — the page is unchanged — and a
 * label the platform does not give is `missing`, never guessed.
 */

import { MAX_PAGES, callOperation } from '@/api';
import type { HealthFigureModel, HealthGroupBy, HealthGroupModel, HealthReportModel } from '@/api/generated';
import { NOT_PUBLISHED, NO_PRINCIPAL, buildVersion } from '@/export';
import type { ExportCell, ExportColumn, ExportRequest, ProvenanceFilter } from '@/export';
import {
  ARTICLE_ATTRIBUTE_COLUMNS, NO_ATTRIBUTES, articleAttributeCells, readHealthAttributes, type HealthAttributes,
} from '@/planning/health-attributes';
import type { ExportScope } from '../../manifest';

import {
  GROUP_PAGE_SIZE, REPORT_OPERATION, measureWords, readHealthScope, reportQuery, type HealthReportId, type HealthScope,
} from './reports';

const PLATFORM_VERSION = buildVersion();

/** The export ids, one per report and level — `<slug>.<route>.<surface>`. */
export const HEALTH_EXPORT_ID: Readonly<Record<HealthReportId, string>> = {
  availability: 'strategic.healthAvailability.groups',
  coverage: 'strategic.healthCoverage.groups',
  'sell-through': 'strategic.healthSellThrough.groups',
  'markdown-exposure': 'strategic.healthMarkdown.groups',
};

export const HEALTH_BY_EXPORT_ID: Readonly<Record<HealthReportId, string>> = {
  availability: 'strategic.healthAvailability.by',
  coverage: 'strategic.healthCoverage.by',
  'sell-through': 'strategic.healthSellThrough.by',
  'markdown-exposure': 'strategic.healthMarkdown.by',
};

/** The measures each report's groups carry (bata-platform ADR 9053), in the platform's order. */
export const REPORT_GROUP_MEASURES: Readonly<Record<HealthReportId, readonly string[]>> = {
  availability: ['cells_served_in_stock', 'cells_out_or_broken', 'broken_size_runs'],
  coverage: ['cells_low_on_cover'],
  'sell-through': ['sell_through_season_to_date', 'full_price_sell_through', 'full_price_share_of_sales', 'units_sold_week', 'sell_through_week'],
  'markdown-exposure': ['markdown_exposure_units', 'markdown_exposure_at_cost', 'zona_sales_stock_units', 'outlet_stock_units'],
};

const missing: ExportCell = { kind: 'missing', state: 'not-reported' };
const number = (value: number | null | undefined): ExportCell => (value === null || value === undefined ? missing : { kind: 'number', raw: value });
const text = (value: string | null | undefined): ExportCell => (value === null || value === undefined || value === '' ? missing : { kind: 'text', value });
const code = (value: string | null | undefined): ExportCell => (value === null || value === undefined || value === '' ? missing : { kind: 'label', value, code: value });

/** A figure's value cell: a share as a percentage, an amount as money, a count as a number. */
function valueCell(figure: HealthFigureModel | undefined): ExportCell {
  if (figure === undefined || figure.value === null) return missing;
  if (figure.unit === 'share') return { kind: 'percent', raw: figure.value };
  if (figure.unit === 'amount' && figure.currencyCode !== null) return { kind: 'money', raw: figure.value, currency: figure.currencyCode };
  return { kind: 'number', raw: figure.value };
}

/**
 * The columns of one report's file: the group, then six per measure; availability adds the full-price verdict.
 * By article, the article's five labels follow its name.
 */
export function healthColumns(report: HealthReportId, by?: HealthGroupBy): readonly ExportColumn[] {
  const columns: ExportColumn[] = [
    { key: 'country_key', headerKey: 'strategic.column.country', kind: 'text', descriptionKey: 'strategic.columnGuide.country' },
    { key: 'group_by', headerKey: 'strategic.health.export.groupBy', kind: 'label', descriptionKey: 'strategic.health.columnGuide.groupBy' },
    { key: 'group_key', headerKey: 'strategic.health.export.groupKey', kind: 'text', descriptionKey: 'strategic.health.columnGuide.groupKey' },
    { key: 'group_label', headerKey: 'strategic.health.export.groupLabel', kind: 'text', descriptionKey: 'strategic.health.columnGuide.groupLabel' },
  ];
  if (by === 'article') columns.push(...ARTICLE_ATTRIBUTE_COLUMNS);
  for (const measure of REPORT_GROUP_MEASURES[report]) {
    const labelKey = measureWords(measure).labelKey;
    const values = { measure: measure };
    columns.push(
      { key: measure, headerKey: labelKey, kind: 'number', descriptionKey: measureWords(measure).helpKey },
      { key: `${measure}_numerator`, headerKey: 'strategic.health.export.numerator', headerValues: values, kind: 'number', descriptionKey: 'strategic.health.columnGuide.numerator', descriptionValues: values },
      { key: `${measure}_denominator`, headerKey: 'strategic.health.export.denominator', headerValues: values, kind: 'number', descriptionKey: 'strategic.health.columnGuide.denominator', descriptionValues: values },
      { key: `${measure}_uncovered`, headerKey: 'strategic.health.export.uncovered', headerValues: values, kind: 'number', descriptionKey: 'strategic.health.columnGuide.uncovered', descriptionValues: values },
      { key: `${measure}_target`, headerKey: 'strategic.health.export.target', headerValues: values, kind: 'number', descriptionKey: 'strategic.health.columnGuide.target', descriptionValues: values },
      { key: `${measure}_rag`, headerKey: 'strategic.health.export.rag', headerValues: values, kind: 'label', descriptionKey: 'strategic.health.columnGuide.rag', descriptionValues: values },
      { key: `${measure}_reason`, headerKey: 'strategic.health.export.reason', headerValues: values, kind: 'label', descriptionKey: 'strategic.health.columnGuide.reason', descriptionValues: values },
    );
  }
  if (report === 'availability') {
    columns.push(
      { key: 'full_price', headerKey: 'strategic.health.export.fullPrice', kind: 'label', descriptionKey: 'strategic.health.columnGuide.fullPrice' },
      { key: 'ranged_stores', headerKey: 'strategic.health.export.rangedStores', kind: 'integer', descriptionKey: 'strategic.health.columnGuide.rangedStores' },
      { key: 'in_force_stores', headerKey: 'strategic.health.export.inForceStores', kind: 'integer', descriptionKey: 'strategic.health.columnGuide.inForceStores' },
    );
  }
  columns.push(
    { key: 'source_id', headerKey: 'strategic.health.export.source', kind: 'text', descriptionKey: 'strategic.health.columnGuide.source' },
    { key: 'as_of', headerKey: 'strategic.health.export.asOf', kind: 'date', descriptionKey: 'strategic.health.columnGuide.asOf' },
  );
  return columns;
}

/** One group as a row of the file, in `healthColumns` order. Pure. */
export function healthRow(
  report: HealthReportId, data: Pick<HealthReportModel, 'countryKey' | 'sourceId' | 'asOf'>, by: HealthGroupBy, group: HealthGroupModel,
  attributes: HealthAttributes = NO_ATTRIBUTES,
): ExportCell[] {
  const cells: ExportCell[] = [text(data.countryKey), code(by), text(group.groupKey), text(group.label)];
  if (by === 'article') cells.push(...articleAttributeCells(attributes, group.groupKey));
  for (const measure of REPORT_GROUP_MEASURES[report]) {
    const figure = group.figures.find((each) => each.measure === measure && !('fullPrice' in each.breakdown));
    cells.push(
      valueCell(figure),
      number(figure?.numerator),
      number(figure?.denominator),
      number(figure?.uncovered),
      number(figure?.target?.value),
      code(figure?.target?.rag),
      code(figure?.absentReason),
    );
  }
  if (report === 'availability') {
    const verdict = group.figures.find((each) => each.measure === 'full_price_availability' && 'fullPrice' in each.breakdown);
    cells.push(
      verdict === undefined ? missing : code(verdict.breakdown['fullPrice'] === 1 ? 'full_price' : verdict.absentReason ?? 'not_full_price'),
      verdict === undefined ? missing : { kind: 'integer', raw: verdict.breakdown['rangedStores'] ?? 0 },
      verdict === undefined ? missing : { kind: 'integer', raw: verdict.breakdown['inForceStores'] ?? 0 },
    );
  }
  cells.push(text(data.sourceId), data.asOf === null ? missing : { kind: 'date', iso: data.asOf });
  return cells;
}

/** Every group of one question, page after page in the platform's order, as far as `MAX_PAGES`. */
export async function readEveryGroup(report: HealthReportId, scope: HealthScope, by: HealthGroupBy): Promise<{ readonly data: HealthReportModel | null; readonly groups: readonly HealthGroupModel[] }> {
  const groups: HealthGroupModel[] = [];
  let data: HealthReportModel | null = null;
  let cursor: string | null = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const params = reportQuery(report, scope, { by, department: scope.department, cursor, limit: GROUP_PAGE_SIZE });
    if (params === null) break;
    const answer: HealthReportModel = await callOperation(REPORT_OPERATION[report], params);
    data ??= answer;
    groups.push(...(answer.groups?.items ?? []));
    if (answer.groups?.hasMore !== true || answer.groups.nextCursor === null) break;
    cursor = answer.groups.nextCursor;
  }
  return { data, groups };
}

/** The file of one report at one level — the route's export, re-reading the page's scope. */
export async function healthRequest(report: HealthReportId, level: 0 | 1, scope?: ExportScope): Promise<ExportRequest> {
  const parsed = readHealthScope(scope?.search ?? '');
  const by: HealthGroupBy = level === 0 ? 'department_family' : (parsed.by === 'cluster' && parsed.department === null ? 'department_family' : parsed.by ?? 'department_family');
  const read = parsed.country === null ? { data: null, groups: [] } : await readEveryGroup(report, level === 0 ? { ...parsed, department: null } : parsed, by);
  const attributes = by === 'article' && read.data !== null
    ? await readHealthAttributes(parsed.country, read.data.asOf, read.groups.map((group) => group.groupKey))
    : NO_ATTRIBUTES;
  const rows = read.data === null ? [] : read.groups.map((group) => healthRow(report, read.data as HealthReportModel, by, group, attributes));
  const filters: ProvenanceFilter[] = [];
  if (parsed.country !== null) filters.push({ field: 'country_key', operator: 'equals', value: parsed.country });
  filters.push({ field: 'group_by', operator: 'equals', value: by });
  if (level === 1 && parsed.department !== null) filters.push({ field: 'department_family', operator: 'equals', value: parsed.department });
  if (read.data?.sourceId !== null && read.data?.sourceId !== undefined) filters.push({ field: 'source_id', operator: 'equals', value: read.data.sourceId });
  const columns = healthColumns(report, by);
  return {
    id: level === 0 ? HEALTH_EXPORT_ID[report] : HEALTH_BY_EXPORT_ID[report],
    titleKey: level === 0 ? 'strategic.health.export.titleFamilies' : 'strategic.health.export.titleGroups',
    columns,
    rows: () => rows,
    rowCount: rows.length,
    provenance: {
      filters,
      hierarchyNode: NOT_PUBLISHED,
      timeWindow: NOT_PUBLISHED,
      scenario: NOT_PUBLISHED,
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: REPORT_GROUP_MEASURES[report].map((measure) => ({ measure: `health.${measure}`, definition: HEALTH_DEFINITION, registerVersion: '' })),
      generatedAt: new Date().toISOString(),
      generatedBy: NO_PRINCIPAL,
      platformVersion: PLATFORM_VERSION,
    },
    perimeter: {
      filters,
      sort: [],
      visibleColumns: columns.map((column) => column.key),
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

const HEALTH_DEFINITION = 'One Health figure per group as the platform read computes it (bata-platform ADR 9053): every share re-divided from summed parts, so a group is weighted by its base; a figure with no base is absent with its reason, never zero; the target and its colour are the management target in force for the market (ADR 9054), absent when none is.';

/** The route resolvers the manifest registers. */
export const healthExport = (report: HealthReportId) => (scope?: ExportScope): Promise<ExportRequest> => healthRequest(report, 0, scope);
export const healthByExport = (report: HealthReportId) => (scope?: ExportScope): Promise<ExportRequest> => healthRequest(report, 1, scope);

